import { rtcLog } from './debug';
import { createIceCandidate, normalizeCandidate, summarizeCandidate } from './ice';
import type { CallBootstrap, CallType } from './types';

export type RtcCallbacks = {
  onIceCandidate?: (candidate: RTCIceCandidate) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onConnectionStateChange?: (state: RTCPeerConnectionState) => void;
};

function summarizeSdp(description: RTCSessionDescriptionInit) {
  const sdp = description.sdp ?? '';
  return {
    type: description.type,
    hasAudio: /\nm=audio\s/i.test(`\n${sdp}`),
    hasVideo: /\nm=video\s/i.test(`\n${sdp}`),
    audioDirections: [...sdp.matchAll(/m=audio[\s\S]*?(?=\nm=|\s*$)/g)]
      .map((match) => match[0].match(/a=(sendrecv|sendonly|recvonly|inactive)/)?.[1] ?? 'missing'),
    videoDirections: [...sdp.matchAll(/m=video[\s\S]*?(?=\nm=|\s*$)/g)]
      .map((match) => match[0].match(/a=(sendrecv|sendonly|recvonly|inactive)/)?.[1] ?? 'missing'),
  };
}

function getMediaDevices() {
  if (typeof navigator === 'undefined') {
    return null;
  }
  return navigator.mediaDevices ?? null;
}

export class CallRtcSession {
  private peer: RTCPeerConnection | null = null;
  private peerPromise: Promise<RTCPeerConnection> | null = null;
  private localStream: MediaStream | null = null;
  private localStreamPromise: Promise<MediaStream> | null = null;
  private remoteStream: MediaStream | null = null;
  private closed = false;
  private pendingIceCandidates: RTCIceCandidateInit[] = [];

  constructor(
    private readonly bootstrap: CallBootstrap,
    private readonly type: CallType,
    private readonly callbacks: RtcCallbacks,
  ) {
    if (typeof window !== 'undefined') {
      const debugWindow = window as typeof window & {
        __locsRtcSessions?: CallRtcSession[];
      };
      debugWindow.__locsRtcSessions = debugWindow.__locsRtcSessions ?? [];
      debugWindow.__locsRtcSessions.push(this);
    }
  }

  async ensureLocalStream() {
    if (this.closed) {
      throw new Error('Call session is closed');
    }
    if (this.localStream) {
      return this.localStream;
    }
    if (!this.localStreamPromise) {
      const mediaDevices = getMediaDevices();
      if (!mediaDevices?.getUserMedia) {
        const error = new Error('当前浏览器或访问方式不支持麦克风/摄像头，请使用 localhost、HTTPS 或支持媒体权限的浏览器。');
        rtcLog('error', 'getUserMedia unavailable', {
          protocol: typeof window !== 'undefined' ? window.location.protocol : 'unknown',
          hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
          hasNavigator: typeof navigator !== 'undefined',
          hasMediaDevices: !!mediaDevices,
        });
        throw error;
      }

      this.localStreamPromise = mediaDevices.getUserMedia({
        audio: true,
        video: this.type === 'video',
      }).then((stream) => {
        rtcLog('info', 'local stream acquired', {
          type: this.type,
          tracks: stream.getTracks().map((track) => ({
            kind: track.kind,
            label: track.label,
            enabled: track.enabled,
            muted: track.muted,
            readyState: track.readyState,
          })),
        });
        if (this.closed) {
          stream.getTracks().forEach((track) => track.stop());
          throw new Error('Call session is closed');
        }
        this.localStream = stream;
        return stream;
      }).catch((error) => {
        this.localStreamPromise = null;
        throw error;
      });
    }
    return this.localStreamPromise;
  }

  async ensurePeer() {
    if (this.closed) {
      throw new Error('Call session is closed');
    }
    if (this.peer) {
      return this.peer;
    }
    if (!this.peerPromise) {
      this.peerPromise = this.createPeer().catch((error) => {
        this.peerPromise = null;
        this.peer?.close();
        this.peer = null;
        this.remoteStream = null;
        throw error;
      });
    }
    return this.peerPromise;
  }

  private async createPeer() {
    const peer = new RTCPeerConnection({
      iceServers: this.bootstrap.iceServers,
    });
    this.peer = peer;
    this.remoteStream = new MediaStream();
    rtcLog('info', 'peer created', {
      type: this.type,
      iceServers: this.bootstrap.iceServers,
    });

    peer.ontrack = (event) => {
      rtcLog('info', 'remote track received', {
        kind: event.track.kind,
        label: event.track.label,
        enabled: event.track.enabled,
        muted: event.track.muted,
        readyState: event.track.readyState,
        streamTrackCounts: event.streams.map((stream) => ({
          id: stream.id,
          audio: stream.getAudioTracks().length,
          video: stream.getVideoTracks().length,
        })),
      });
      for (const track of event.streams[0]?.getTracks?.() ?? [event.track]) {
        this.remoteStream?.addTrack(track);
      }
      if (this.remoteStream) {
        this.callbacks.onRemoteStream?.(this.remoteStream);
      }
    };

    peer.onicecandidate = (event) => {
      if (event.candidate) {
        rtcLog('info', 'local ice candidate', summarizeCandidate(event.candidate.candidate));
        this.callbacks.onIceCandidate?.(event.candidate);
      } else {
        rtcLog('info', 'local ice gathering complete');
      }
    };

    peer.onicecandidateerror = (event) => {
      rtcLog('warn', 'ice candidate error', {
        address: event.address,
        port: event.port,
        url: event.url,
        errorCode: event.errorCode,
        errorText: event.errorText,
      });
    };

    peer.onconnectionstatechange = () => {
      rtcLog('info', 'peer connection state', peer.connectionState);
      this.callbacks.onConnectionStateChange?.(peer.connectionState);
    };

    peer.oniceconnectionstatechange = () => {
      rtcLog('info', 'ice connection state', peer.iceConnectionState);
      if (peer.iceConnectionState === 'connected' || peer.iceConnectionState === 'completed') {
        void this.logSelectedCandidatePair(peer);
        this.callbacks.onConnectionStateChange?.('connected');
      } else if (
        peer.iceConnectionState === 'failed'
        || peer.iceConnectionState === 'disconnected'
        || peer.iceConnectionState === 'closed'
      ) {
        void this.logIceStats(peer, 'ice state failed');
        this.callbacks.onConnectionStateChange?.('failed');
      }
    };

    if (this.closed) {
      peer.close();
      throw new Error('Call session is closed');
    }

    const stream = await this.ensureLocalStream();
    if (this.closed) {
      peer.close();
      throw new Error('Call session is closed');
    }
    for (const track of stream.getTracks()) {
      peer.addTrack(track, stream);
      rtcLog('info', 'local track added', {
        kind: track.kind,
        label: track.label,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      });
    }

    return peer;
  }

  getLocalStream() {
    return this.localStream;
  }

  getRemoteStream() {
    return this.remoteStream;
  }

  async getDebugSnapshot() {
    const peer = this.peer;
    const stats: RTCStats[] = [];
    if (peer) {
      const report = await peer.getStats();
      report.forEach((entry) => {
        if (
          entry.type === 'candidate-pair'
          || entry.type === 'local-candidate'
          || entry.type === 'remote-candidate'
          || entry.type === 'inbound-rtp'
          || entry.type === 'outbound-rtp'
          || entry.type === 'transport'
        ) {
          stats.push(entry);
        }
      });
    }
    return {
      closed: this.closed,
      type: this.type,
      hasPeer: Boolean(peer),
      connectionState: peer?.connectionState,
      iceConnectionState: peer?.iceConnectionState,
      iceGatheringState: peer?.iceGatheringState,
      signalingState: peer?.signalingState,
      localDescriptionType: peer?.localDescription?.type,
      remoteDescriptionType: peer?.remoteDescription?.type,
      transceivers: peer?.getTransceivers().map((transceiver) => ({
        mid: transceiver.mid,
        direction: transceiver.direction,
        currentDirection: transceiver.currentDirection,
        senderTrack: transceiver.sender.track ? {
          kind: transceiver.sender.track.kind,
          enabled: transceiver.sender.track.enabled,
          muted: transceiver.sender.track.muted,
          readyState: transceiver.sender.track.readyState,
        } : null,
        receiverTrack: transceiver.receiver.track ? {
          kind: transceiver.receiver.track.kind,
          enabled: transceiver.receiver.track.enabled,
          muted: transceiver.receiver.track.muted,
          readyState: transceiver.receiver.track.readyState,
        } : null,
      })),
      localTracks: this.localStream?.getTracks().map((track) => ({
        kind: track.kind,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      })) ?? [],
      remoteTracks: this.remoteStream?.getTracks().map((track) => ({
        kind: track.kind,
        enabled: track.enabled,
        muted: track.muted,
        readyState: track.readyState,
      })) ?? [],
      stats,
    };
  }

  async createOffer() {
    const peer = await this.ensurePeer();
    if (this.closed) {
      throw new Error('Call session is closed');
    }
    const offer = await peer.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: this.type === 'video',
    });
    rtcLog('info', 'offer created', summarizeSdp(offer));
    await peer.setLocalDescription(offer);
    rtcLog('info', 'local description set', summarizeSdp(offer));
    return offer;
  }

  async acceptOffer(offer: RTCSessionDescriptionInit) {
    const peer = await this.ensurePeer();
    if (this.closed) {
      throw new Error('Call session is closed');
    }
    rtcLog('info', 'applying remote offer', summarizeSdp(offer));
    await peer.setRemoteDescription(offer);
    rtcLog('info', 'remote offer set', summarizeSdp(offer));
    const answer = await peer.createAnswer();
    rtcLog('info', 'answer created', summarizeSdp(answer));
    await peer.setLocalDescription(answer);
    rtcLog('info', 'local answer set', summarizeSdp(answer));
    window.setTimeout(() => {
      void this.flushPendingIceCandidates(peer).catch((error) => {
        rtcLog('error', 'flush queued remote ice candidates failed', error);
      });
    }, 0);
    return answer;
  }

  async acceptAnswer(answer: RTCSessionDescriptionInit) {
    const peer = await this.ensurePeer();
    if (this.closed) {
      throw new Error('Call session is closed');
    }
    rtcLog('info', 'applying remote answer', summarizeSdp(answer));
    await peer.setRemoteDescription(answer);
    rtcLog('info', 'remote answer set', summarizeSdp(answer));
    await this.flushPendingIceCandidates(peer);
  }

  async addIceCandidate(candidate: RTCIceCandidateInit) {
    const peer = await this.ensurePeer();
    if (this.closed) {
      return;
    }
    const normalizedCandidate = normalizeCandidate(candidate);
    const iceCandidate = createIceCandidate(normalizedCandidate);
    if (!peer.remoteDescription) {
      rtcLog('info', 'queue remote ice before description', summarizeCandidate(normalizedCandidate.candidate));
      this.pendingIceCandidates.push(normalizedCandidate);
      return;
    }
    rtcLog('info', 'add remote ice candidate', summarizeCandidate(normalizedCandidate.candidate));
    try {
      await peer.addIceCandidate(iceCandidate);
      void this.logIceStats(peer, 'after add remote ice candidate');
    } catch (error) {
      rtcLog('error', 'add remote ice candidate failed', {
        candidate: summarizeCandidate(normalizedCandidate.candidate),
        error,
      });
      throw error;
    }
  }

  private async flushPendingIceCandidates(peer: RTCPeerConnection) {
    if (!peer.remoteDescription || this.pendingIceCandidates.length === 0) {
      return;
    }
    const candidates = this.pendingIceCandidates.splice(0);
    rtcLog('info', 'flush queued remote ice candidates', candidates.length);
    for (const candidate of candidates) {
      if (this.closed) {
        return;
      }
      rtcLog('info', 'add queued remote ice candidate', summarizeCandidate(candidate.candidate));
      try {
        await peer.addIceCandidate(createIceCandidate(candidate));
        void this.logIceStats(peer, 'after add queued remote ice candidate');
      } catch (error) {
        rtcLog('error', 'add queued remote ice candidate failed', {
          candidate: summarizeCandidate(candidate.candidate),
          error,
        });
        throw error;
      }
    }
  }

  private async logSelectedCandidatePair(peer: RTCPeerConnection) {
    try {
      const stats = await peer.getStats();
      let selectedPair: RTCStats | undefined;
      stats.forEach((report) => {
        if (report.type === 'candidate-pair' && (report as any).selected) {
          selectedPair = report;
        }
      });
      rtcLog('info', 'selected candidate pair', selectedPair ?? 'not-found');
    } catch (error) {
      rtcLog('warn', 'selected candidate pair stats failed', error);
    }
  }

  private async logIceStats(peer: RTCPeerConnection, reason: string) {
    try {
      const stats = await peer.getStats();
      const candidates: RTCStats[] = [];
      const candidatePairs: RTCStats[] = [];
      stats.forEach((report) => {
        if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
          candidates.push(report);
        }
        if (report.type === 'candidate-pair') {
          candidatePairs.push(report);
        }
      });
      rtcLog('info', 'ice stats snapshot', {
        reason,
        candidates,
        candidatePairs,
      });
    } catch (error) {
      rtcLog('warn', 'ice stats snapshot failed', error);
    }
  }

  close() {
    rtcLog('info', 'close session');
    this.closed = true;
    if (this.peer) {
      this.peer.ontrack = null;
      this.peer.onicecandidate = null;
      this.peer.onconnectionstatechange = null;
      this.peer.oniceconnectionstatechange = null;
      this.peer.close();
    }
    this.peer = null;
    this.peerPromise = null;
    this.pendingIceCandidates = [];
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    this.localStreamPromise = null;
    this.remoteStream?.getTracks().forEach((track) => track.stop());
    this.remoteStream = null;
  }
}
